import { Grid, IconButton, Typography, Theme, Box, Dialog, Skeleton } from '@mui/material';
import { Form, Formik, FormikProps } from '../../../lib/formik/Formik';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { ReportsRightLayout } from '../../InvestorReport/index';
import { MFSubmitButton } from '../../../lib/formik';
import { useEffect, useRef, useState } from 'react';
import dayjs from "../../../utils/dayjs";
import { ReportsListObj, ReportsListRes } from '../../../redux-store/types/api-types';
import MFSelectField from '../../../lib/formik/SelectField';
import * as Yup from 'yup';
import { DayJsDatePicker } from '../../onboarding/newDatePicker';
import { RootStateType } from '../../../redux-store/reducers';
import { getCGInvestorReport, getCGStatementReport, getReportsAnalystInvestorList, getReportsInvestorList } from '../../../redux-store/actions/reports';
import { DialogCloseIcon, YellowWarning } from '../../../assets/SvgIcons';
import { reportDownload } from '../../Reports/report-utils';
import { isModuleAllowed, maskInfo } from '../../../utils/utilityFunctions';
import FormikSearchDropdown from '../../../lib/formik/FormikSearchDropdown';
import { CONTENT_TYPE, MPOWER_ACCOUNT_TYPE_MASTER, REPORTS_DOWNLOAD_OPTION, USER_ROLES } from '../../../utils/constant';

interface Values {
    selectedInvestor: ReportsListObj | string | null;
    startDate: string;
    endDate: string;
    downloadOption: string;
}

export function CapitalGainStatement({
    handleSideSectionDisabled
}: {
    handleSideSectionDisabled: (value: boolean) => void
}): JSX.Element {
    const formikRef = useRef<FormikProps<Values>>(null);

    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const currentDate = dayjs();
    const [loading, setLoading] = useState<boolean>(false);
    const { iwappRmId = "", investorClientId = "", panNo: currentInvestorPanNo = "", investorName: currentInvestorName = "" } = useSelector((state: RootStateType) => state.id);
    const { role = '', subRole = '' } = useSelector((state: RootStateType) => state.auth);

    const [investorListData, setInvestorListData] = useState<ReportsListRes>({} as ReportsListRes);
    const [isNoInvestorDialogVisible, setNoInvestorDialogVisible] = useState<boolean>(false)
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const { isNavbarInfoLoading, businessDate } = useSelector((state: RootStateType) => state.navbarInfo);

    const isInvestor = isModuleAllowed({ value: role, compareArr: [USER_ROLES.INVESTOR] })

    const isPdfDownloadAllowed = true

    const fetchInvestorList = async () => {
        try {
            setLoading(true);

            let response = {} as ReportsListRes

            if ([USER_ROLES.ANALYST].includes(role)) {
                response = await (dispatch(getReportsAnalystInvestorList({
                    analystId: `${iwappRmId}`
                })) as unknown as Promise<ReportsListRes>);
            } else {
                response = await (dispatch(getReportsInvestorList({
                    rmId: `${iwappRmId}`,
                    reportName: "capital-gain-statement"
                })) as unknown as Promise<ReportsListRes>);
            }

            setInvestorListData(response);

            if (!response?.isSuccess) {
                setNoInvestorDialogVisible(true)
            }
        } catch (e) {
            console.log((e as Error).message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isInvestor) {
            fetchInvestorList();
        }
    }, [])

    const todayDate = new Date();
    const formattedBusinessDate = businessDate?.length ? dayjs(businessDate, "DD/MM/YYYY").format("YYYY-MM-DD") : dayjs(todayDate).format("YYYY-MM-DD");

    const initialValues: Values = {
        selectedInvestor: isInvestor ? investorClientId : null,
        startDate: formattedBusinessDate,
        endDate: formattedBusinessDate,
        downloadOption: isPdfDownloadAllowed ? '' : "excel"
    };

    const cgValidationSchema = Yup.object().shape({
        selectedInvestor: isInvestor ?
            Yup
                .string()
                .nullable() :
            Yup
                .object()
                .nullable()
                .test(
                    "investor-required",
                    "Please select an investor",
                    function (value) { return !!value }
                ),
        startDate: Yup
            .date()
            .required('From Date is required')
            .typeError('From Date is required')

            // .min(new Date("1950-01-01"), "From Date should not be less than 01/01/1950")
            .test('min-date', "From Date should not be less than 01/01/1950", function (value) {
                if (!value) return true;
                return dayjs(value).isAfter(dayjs('1950-01-01'), 'day');
            })

            // .max(formattedBusinessDate, "From Date should not be greater than system's date")
            .test('max-today', "From Date should not be greater than system's date", function (value) {
                if (!value) return true;
                return dayjs(value).isSameOrBefore(dayjs(formattedBusinessDate), 'day');
            })

            .test('fromDateTest', 'From Date cannot be greater than To Date', function (value) {
                // const { endDate } = this.parent;
                // return !endDate || !value || value <= endDate;

                const { endDate } = this.parent;
                if (!endDate || !value) return true;
                return dayjs(value).isSameOrBefore(dayjs(endDate), 'day');
            }),
        endDate: Yup
            .date()
            .required('To Date is required')
            .typeError('To Date is required')

            // .min(new Date("1950-01-01"), "To Date should not be less than 01/01/1950")
            .test('min-date', "To Date should not be less than 01/01/1950", function (value) {
                if (!value) return true;
                return dayjs(value).isAfter(dayjs('1950-01-01'), 'day');
            })

            // .max(formattedBusinessDate, "To Date should not be greater than system's date")
            .test('max-today', "To Date should not be greater than system's date", function (value) {
                if (!value) return true;
                return dayjs(value).isSameOrBefore(dayjs(formattedBusinessDate), 'day');
            })

            .test('toDateTest', 'To Date cannot be less than From Date', function (value) {
                // const { startDate } = this.parent;
                // return !startDate || !value || value >= startDate;

                const { startDate } = this.parent;
                if (!startDate || !value) return true;
                return dayjs(value).isSameOrAfter(dayjs(startDate), 'day');
            }),
        downloadOption: Yup.string().required('Please select download option'),
    })


    const handleSubmit = async ({
        selectedInvestor,
        startDate,
        endDate,
        downloadOption
    }: Values) => {
        try {
            setIsDownloading(true)
            handleSideSectionDisabled(true)
            const nonInvestorPayload = {
                investorName: (selectedInvestor as ReportsListObj)?.customerName ?? '',
                start: dayjs(startDate, "YYYY-MM-DD").format("DD/MM/YYYY"),
                end: dayjs(endDate, "YYYY-MM-DD").format("DD/MM/YYYY"),

                //adding clientCode
                clientCode: (selectedInvestor as ReportsListObj)?.clientCode ?? '',
                isPDF: (`${downloadOption ?? ""}`.toLowerCase()) === 'pdf',
                panNo: (selectedInvestor as ReportsListObj)?.panNo ?? ''
            }

            const investorPayload = {
                clientCode: (selectedInvestor as string) ?? "",
                rmId: `${iwappRmId ?? ""}`,
                start: dayjs(startDate, "YYYY-MM-DD").format("DD/MM/YYYY"),
                end: dayjs(endDate, "YYYY-MM-DD").format("DD/MM/YYYY"),
                isPDF: (`${downloadOption ?? ""}`.toLowerCase()) === 'pdf',
                investorName: (currentInvestorName ?? ""),
                panNo: (currentInvestorPanNo ?? "")
            }

            const response = isInvestor ?
                await (dispatch(getCGInvestorReport(investorPayload)) as unknown as Promise<any>) :
                await (dispatch(getCGStatementReport(nonInvestorPayload)) as unknown as Promise<any>)

            if (response && [CONTENT_TYPE.PDF, CONTENT_TYPE.EXCEL].includes(response.type)) {
                enqueueSnackbar('File downloaded successfully.', {
                    variant: 'success',
                    autoHideDuration: 2000,
                });
                reportDownload(response as unknown as Blob, `CGStatementReport.${response.type === CONTENT_TYPE.PDF ? 'pdf' : 'xlsx'}`);
            } else {
                enqueueSnackbar('File download failed!.', {
                    variant: 'error',
                    autoHideDuration: 2000,
                });
            }

        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setIsDownloading(false)
            handleSideSectionDisabled(false)
        }
    }

    const handleNoInvestorDialogClose = () => {
        setNoInvestorDialogVisible(false)
    }

    const handleInvestorChange = (selectedObj: Record<string, any>) => {
        if (!formikRef.current) return

        const { values, setValues } = formikRef.current ?? {}

        if (!selectedObj) {
            setValues({
                ...values,
                selectedInvestor: null,
                startDate: formattedBusinessDate,
                endDate: formattedBusinessDate,
                downloadOption: isPdfDownloadAllowed ? '' : "excel"
            })

            return
        }

        setValues({
            ...values,
            selectedInvestor: selectedObj as ReportsListObj,
            startDate: formattedBusinessDate,
            endDate: formattedBusinessDate,
            downloadOption: isPdfDownloadAllowed ? '' : "excel"
        })
    }

    return (
        <>
            <ReportsRightLayout>
                <Typography
                    variant='h2'
                    sx={{
                        color: 'text.primary',
                        lineHeight: '25px',
                        letterSpacing: '0.01px',
                        mb: '40px'
                    }}>
                    Capital Gain Statement
                </Typography>
                <Formik
                    innerRef={formikRef}
                    validationSchema={cgValidationSchema}
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    enableReinitialize={true}
                >
                    {({ values }) => {
                        return (
                            <Form>
                                <Grid container columnSpacing={3} rowSpacing={1.5} >
                                    {!isInvestor && (
                                        <Grid item xs={12} lg={6}>
                                            {(loading || isNavbarInfoLoading) && <Skeleton
                                                animation={'wave'}
                                                height={90}

                                            />}
                                            {(!loading && !isNavbarInfoLoading) && (
                                                <FormikSearchDropdown
                                                    name='selectedInvestor'
                                                    label='Select Investor *'
                                                    options={(investorListData?.result ?? [])?.map((obj: ReportsListObj) => ({
                                                        ...obj,
                                                        label: `${maskInfo(obj.customerName)} (${obj?.clientCode ?? ""}) ${obj?.investorType ? "(" + obj?.investorType + ")" : ""} ${`${obj?.isFamilyHead ?? ""}`.toLowerCase() === "y" ? "(Head Of Family)" : ""} ${MPOWER_ACCOUNT_TYPE_MASTER[obj?.accountType ?? ""] ? " - " + MPOWER_ACCOUNT_TYPE_MASTER[obj.accountType] : ""}`,
                                                        value: obj.clientCode
                                                    }))}
                                                    onChange={handleInvestorChange}
                                                    disabled={loading || isDownloading || isNavbarInfoLoading}
                                                />
                                            )}
                                        </Grid>
                                    )}
                                    <Grid item xs={12} lg={6}>
                                        {(loading || isNavbarInfoLoading) && <Skeleton
                                            animation={'wave'}
                                            height={90}

                                        />}
                                        {(!loading && !isNavbarInfoLoading) && (
                                            <DayJsDatePicker
                                                id=""
                                                name='startDate'
                                                label="From Date *"
                                                maxDate={new Date(formattedBusinessDate)}
                                                disabled={loading || isDownloading || isNavbarInfoLoading}
                                                currentDate={formattedBusinessDate}
                                                resetDate
                                            />
                                        )}
                                    </Grid>
                                    <Grid item xs={12} lg={6}>
                                        {(loading || isNavbarInfoLoading) && <Skeleton
                                            animation={'wave'}
                                            height={90}

                                        />}
                                        {(!loading && !isNavbarInfoLoading) && (
                                            <DayJsDatePicker
                                                id=""
                                                name='endDate'
                                                label="To Date *"
                                                minDate={new Date(values.startDate)}
                                                maxDate={new Date(formattedBusinessDate)}
                                                disabled={loading || isDownloading || isNavbarInfoLoading}
                                                currentDate={formattedBusinessDate}
                                                resetDate
                                            />
                                        )}
                                    </Grid>
                                    {isPdfDownloadAllowed && (
                                        <Grid item xs={12} lg={6}>
                                            {(loading || isNavbarInfoLoading) && <Skeleton
                                                animation={'wave'}
                                                height={90}

                                            />}
                                            {(!loading && !isNavbarInfoLoading) && <MFSelectField
                                                name='downloadOption'
                                                label="Select Download Option *"
                                                items={REPORTS_DOWNLOAD_OPTION}
                                                disabled={isDownloading}
                                            />}
                                        </Grid>
                                    )}
                                    <Grid item container columnSpacing={3} justifyContent={"flex-end"} sx={{ mt: 2 }}>
                                        <Grid item xs={12} lg={6}>
                                            {(loading || isNavbarInfoLoading) && <Skeleton
                                                animation={'wave'}
                                                height={90}

                                            />}
                                            {(!loading && !isNavbarInfoLoading) && <MFSubmitButton
                                                label="Download"
                                                loadingEnable={isDownloading}
                                            />}
                                        </Grid>
                                    </Grid>

                                </Grid>
                            </Form>
                        )
                    }}
                </Formik>
            </ReportsRightLayout >
            <Dialog
                onClose={handleNoInvestorDialogClose}
                open={isNoInvestorDialogVisible}
                maxWidth='sm'
                sx={(theme: Theme) => ({
                    '& .MuiPaper-root': {
                        borderRadius: '12px',
                        backgroundColor: theme.palette.common.white,
                        padding: "22px 24px",
                        width: "533px",
                    },
                })}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        rowGap: "8px",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                columnGap: "12px",
                                alignItems: "center"
                            }}
                        >
                            <YellowWarning />
                            <Typography
                                variant="h2"
                                sx={(theme: Theme) => ({ color: theme.palette.text.primary })}
                            >
                                No Investor Found!!
                            </Typography>
                        </Box>
                        <IconButton
                            aria-label="close"
                            onClick={handleNoInvestorDialogClose}
                            sx={{
                                padding: "5px"
                            }}
                        >
                            <DialogCloseIcon />
                        </IconButton>
                    </Box>
                </Box>
            </Dialog>
        </>
    );
};


